import { v4 as uuidv4 } from 'uuid';
import { Row2, type RowOptions } from './row';
import Konva from 'konva';
import { GenericNodeEvents } from './common';
import type { LoadBlockStateParam, Position } from './types';
import { Table } from './table';

const ROW_HEIGHT = 10;
const DEFAULT_BLOCK_WIDTH = 1;
const DEFAULT_BLOCK_HEIGHT = 100;
const DEFAULT_ROWS_PADDING = 10;

export type BlockOptions = {
  defaultRows?: number;
  label?: string;
  stageLayer: Konva.Layer;
  x: number;
  y: number;
};

export type AddRowOptions = Omit<
  RowOptions,
  'blockGroup' | 'label' | 'onWidthChange'
>;

export class Block2 extends GenericNodeEvents<
  Block2,
  LoadBlockStateParam & { position: Position }
> {
  private id!: string;
  private readonly layer: Konva.Layer;
  private readonly group: Konva.Group;
  private container!: Konva.Rect;
  private label: Konva.Text;
  public rows: Row2[];
  public tables: Table[];
  private state: LoadBlockStateParam & { position: Position };

  get nodeId() {
    return this.id;
  }

  get currentState() {
    return this.state;
  }

  constructor(
    layer: Konva.Layer,
    state: LoadBlockStateParam & { position: Position }
  ) {
    const group = new Konva.Group({
      draggable: state.draggable !== undefined ? state.draggable : true,
    });

    super(group, 'block');
    this.target = this;
    this.state = state;

    this.layer = layer;
    this.initContainer();
    this.group = group;
    this.label = new Konva.Text();
    this.rows = [];
    this.tables = [];

    this.group.add(this.container);
    this.layer.add(this.group);
  }

  create() {
    this.group.destroyChildren();
    this.initContainer();

    this.container.moveToTop();

    this.group.setAttrs({
      width: this.container.width(),
      height: this.container.height(),
    });

    this.group.on('transformend', () => {
      this.state.position = this.group.position();
      this.state.rotation = this.group.rotation();

      if (this.onStateChangeCallback) {
        this.onStateChangeCallback(this.state);
      }
    });

    this.group.add(this.container);
    this.container.moveToTop();

    this.id = this.state.id || uuidv4();

    this.group.setAttrs({
      ...this.state.position,
      rotation: this.state.rotation,
    });

    this.createLabel(this.state.label!);

    this.createRows();
    this.createTables();
  }

  update() {
    this.group.setAttrs({
      ...this.state.position,
    });

    this.container.setAttrs({
      width: this.state.width || DEFAULT_BLOCK_WIDTH,
      height: this.state.height || DEFAULT_BLOCK_HEIGHT,
    });

    this.updateLabel(this.state.label!);
    this.updateRows();

    if (this.onStateChangeCallback) {
      this.onStateChangeCallback(this.state);
    }
  }

  public hideRows() {
    this.rows.forEach((row) => {
      row.moveToBottom();
    });
  }

  public showRows() {
    this.rows.forEach((row) => {
      row.moveToTop();
    });
  }

  private initContainer() {
    const height =
      this.state.rows.length * (ROW_HEIGHT + DEFAULT_ROWS_PADDING) +
      DEFAULT_ROWS_PADDING;

    this.container = new Konva.Rect({
      width: this.state.width || DEFAULT_BLOCK_WIDTH,
      opacity: 0.8,
      height: height || DEFAULT_BLOCK_HEIGHT,
      strokeWidth: 2,
    });
  }

  private createRows() {
    const rows = this.state.rows;
    this.rows = [];

    const x = this.container.x();
    for (let i = 0; i < rows.length; i++) {
      const pos = this.rows.length;
      const y =
        pos * (ROW_HEIGHT + DEFAULT_ROWS_PADDING) + DEFAULT_ROWS_PADDING;
      const start = this.state.fromCharCodeStart || 65;
      const label = String.fromCharCode(start + pos);
      const prefix = rows[i].labelPrefix
        ? rows[i].labelPrefix
        : this.state.rowLabelPrefix || '';

      const stairStep = this.state.stairStep || 0;
      const baseX = this.state.direction === 'reverse' ? x : -x;
      rows[i] = {
        ...rows[i],
        label: rows[i].label || prefix + label,
        position: {
          x: baseX + pos * stairStep + (rows[i].xOffset || 0),
          y,
        },
        fill: this.state.fillOnlyRows ? this.state.fill : undefined,
        direction: rows[i].direction || this.state.direction,
      };

      const row = new Row2(this.group, rows[i] as any);
      if (this.onHoverCallback) {
        row.onHover(this.onHoverCallback);
      }

      if (this.onHoverOutCallback) {
        row.onHoverOut(this.onHoverOutCallback);
      }

      if (this.onPressCallback) {
        row.onPress(this.onPressCallback);
      }

      row.create();
      this.state.rows[i] = row.currentState;
      this.rows.push(row);
    }

    this.rows.forEach((row, i) => {
      row.onStateChange((newState) => {
        this.state.rows[i] = newState;
        if (newState.width && newState.width > this.container.width()) {
          this.container.setAttr('width', newState.width);
          this.state.width = newState.width;

          if (this.state.label) {
            this.updateLabel(this.state.label);
          }
        }

        this.updateRows();
        if (this.onStateChangeCallback) {
          this.onStateChangeCallback(this.state);
        }
      });
    });
  }

  private updateRows() {
    const rows = this.state.rows;

    rows.forEach((state) => {
      const rowIndex = this.rows.findIndex((row) => row.nodeId === state.id);
      if (rowIndex < 0) {
        console.warn('UNABLE TO FIND ROW FOR UPDATE: ', state);
      }

      const row = this.rows[rowIndex];
      const pos = rowIndex;
      const y =
        pos * (ROW_HEIGHT + DEFAULT_ROWS_PADDING) + DEFAULT_ROWS_PADDING;
      const containerX = this.container.x();
      const stairStep = this.state.stairStep || 0;
      const rowXOffset = state.xOffset || 0;
      let x: number;
      if (stairStep || this.state.diagonalRows) {
        // Diagonal layout: keep rows left-aligned and offset each by stairStep.
        // (diagonalRows leaves stairStep at 0 — the diagonal comes from the
        // per-seat `seatStair` inside each row.)
        x = containerX + pos * stairStep + rowXOffset;
      } else {
        const shift = (state.width ?? 0) - this.container.width();
        x =
          (this.state.direction === 'reverse'
            ? containerX
            : containerX - shift) + rowXOffset;
      }

      state.direction = state.direction ?? this.state.direction;
      (state as any).position = { x, y };

      row.update(state as any);
    });
  }

  private createTables() {
    const tables = this.state.tables;
    if (!tables) return;

    this.tables = [];

    let nextTableNumber = this.state.fromNumberStart || 1;

    for (const tableState of tables) {
      tableState.label = nextTableNumber.toString();
      tableState.draggable = this.state.draggable;
      const table = new Table(this.group, tableState);
      if (this.onHoverCallback) {
        table.onHover(this.onHoverCallback);
      }

      if (this.onHoverOutCallback) {
        table.onHoverOut(this.onHoverOutCallback);
      }

      if (this.onPressCallback) {
        table.onPress(this.onPressCallback);
      }

      table.create();
      this.tables.push(table);
      nextTableNumber++;
    }

    this.tables.forEach((table, i) => {
      table.onStateChange((newState) => {
        this.state.tables[i] = newState;
      });
    });
  }

  private createLabel(label: string) {
    this.label = new Konva.Text({});
    this.group.add(this.label);

    const t = this.label.setAttrs({
      text: label,
      x: this.container.width() / 2,
      y: -ROW_HEIGHT,
      align: 'center',
      verticalAlign: 'middle',
      rotation: this.state.rotationText,
      fontFamily: 'Geist Mono',
      fontStyle: 'bold',
    });

    t.setAttrs({
      offsetX: t.width() / 2,
      offsetY: t.height() / 2,
    });

    if (this.state.hideLabel) {
      this.label.hide();
    } else {
      this.label.show();
    }
  }

  private updateLabel(label: string) {
    const t = this.label.setAttrs({
      text: label,
      fontFamily: 'Geist Mono',
      fontStyle: 'bold',
      x: this.container.width() / 2,
      y: -ROW_HEIGHT,
      align: 'center',
      verticalAlign: 'middle',
    });

    t.setAttrs({
      offsetX: t.width() / 2,
      offsetY: t.height() / 2,
    });

    if (this.state.hideLabel) {
      this.label.hide();
    } else {
      this.label.show();
    }
  }
}
